'use client'

import type { ReactNode } from 'react'
import { Fragment } from 'react'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  buttonVariants,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { MainNavMenuItem, MainNavRenderLink } from './types'
import { defaultRenderLink } from './render-link'

export interface MainNavAvatarMenuProps {
  name: string
  image: string | null
  /** Under the name in the menu header, e.g. "Premium" or "Starter • Tingkatkan". */
  subtitle?: ReactNode
  /** Rows, one array per group; groups are split by a separator. */
  groups: MainNavMenuItem[][]
  /** `vertical`: avatar above the name (the bars). `horizontal`: side by side (a sidebar footer). */
  direction?: 'vertical' | 'horizontal'
  renderLink?: MainNavRenderLink
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

/**
 * The avatar menu, presentational only. The rows differ per account type (PIB-35): e.g. Anak gets
 * Profil saya · Pindah profil · Keluar, a Guru gets Daftar sekolah · Pengaturan akun saya · Panduan
 * guru · Pusat Bantuan · Keluar, and a teacher in her personal school also "Sekolah saya · Belum
 * dilengkapi [Lengkapi]".
 */
export function MainNavAvatarMenu({
  name,
  image,
  subtitle,
  groups,
  direction = 'vertical',
  renderLink = defaultRenderLink,
  open,
  onOpenChange,
  className,
}: MainNavAvatarMenuProps) {
  const isHorizontal = direction === 'horizontal'

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger
        render={(props) => (
          <Button
            {...props}
            variant='ghost'
            className={cn(
              'srd:relative srd:flex srd:h-auto srd:items-center srd:justify-center srd:gap-1 srd:rounded-2xl srd:p-0 srd:select-none',
              isHorizontal ? 'srd:w-full srd:flex-row' : 'srd:w-auto srd:flex-col',
              className,
            )}
          >
            <span className='srd:p-2'>
              <Avatar size={isHorizontal ? 'default' : 'sm'}>
                <AvatarImage src={image ?? undefined} alt={name} />
                <AvatarFallback>{getInitials(name)}</AvatarFallback>
              </Avatar>
            </span>
            <span
              className={cn(
                typographyVariants({ variant: isHorizontal ? 'label-md' : 'label-sm' }),
                'srd:text-neutral-black srd:min-w-0 srd:truncate srd:text-center',
                isHorizontal ? 'srd:max-w-full' : 'srd:max-w-18',
              )}
            >
              {name}
            </span>
          </Button>
        )}
      />
      <DropdownMenuContent className='srd:w-max! srd:min-w-62! srd:max-w-80 srd:select-none' align='end'>
        <DropdownMenuGroup>
          <DropdownMenuLabel className='srd:px-2 srd:py-2'>
            <div className='srd:flex srd:items-center srd:gap-2.5'>
              <Avatar size='lg'>
                <AvatarImage src={image ?? undefined} alt={name} />
                <AvatarFallback>{getInitials(name)}</AvatarFallback>
              </Avatar>
              <div className='srd:min-w-0 srd:flex-1'>
                <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black srd:truncate')}>
                  {name}
                </p>
                {subtitle && (
                  <div className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>{subtitle}</div>
                )}
              </div>
            </div>
          </DropdownMenuLabel>
        </DropdownMenuGroup>

        {groups.map((group, groupIndex) => (
          <Fragment key={group.map((item) => item.id).join('|') || groupIndex}>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {group.map((item) => (
                <MainNavAvatarMenuRow key={item.id} item={item} renderLink={renderLink} />
              ))}
            </DropdownMenuGroup>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface MainNavAvatarMenuRowProps {
  item: MainNavMenuItem
  renderLink: MainNavRenderLink
}

function MainNavAvatarMenuRow({ item, renderLink }: MainNavAvatarMenuRowProps) {
  const Icon = item.icon

  const body = (
    <>
      {Icon && <Icon className='srd:size-5 srd:shrink-0' />}
      <span className='srd:flex srd:flex-1 srd:flex-col'>
        <span className='srd:whitespace-nowrap'>{item.label}</span>
        {item.description && (
          <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:whitespace-nowrap')}>
            {item.description}
          </span>
        )}
      </span>
    </>
  )

  const action =
    item.action &&
    (item.action.href ? (
      renderLink({
        href: item.action.href,
        className: cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'srd:shrink-0'),
        children: item.action.label,
      })
    ) : (
      <Button
        variant='outline'
        size='sm'
        className='srd:shrink-0'
        onClick={(event) => {
          event.stopPropagation()
          item.action?.onSelect?.()
        }}
      >
        {item.action.label}
      </Button>
    ))

  return (
    <DropdownMenuItem className='srd:cursor-pointer' onClick={item.onSelect}>
      {item.href ? (
        renderLink({
          href: item.href,
          className: 'srd:flex srd:flex-1 srd:items-center srd:gap-2',
          children: body,
        })
      ) : (
        <span className='srd:flex srd:flex-1 srd:items-center srd:gap-2'>{body}</span>
      )}
      {action}
    </DropdownMenuItem>
  )
}
